import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StatusCode } from 'ha/modules/StatusCode';
import { OTHER } from 'ha/constants/pageNames';
import { onPageMount } from 'ha/modules/Analytics/actions';
import { FontInfo } from 'ha/components/LoadFonts';
import {
  getCurrentLanguage,
  getIsAuthenticated,
} from 'ha/modules/AuthLogic/selectors';
import { useLocation } from 'ha/helpers/router';
import { getFromStorage, removeFromStorage } from 'ha/utils/sessionStorage';
import { trigger as triggerHotjar } from 'ha/modules/Hotjar/actions';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { useServices } from 'ha/services';

import PageLayout from '../components/PageLayout';
import { PageProvider } from '../contexts/PageContext';

interface Props {
  noFollowFooter?: boolean;
  pageBanner?: React.ReactNode;
  globalBanner?: React.ReactNode;
  category?: string;
  hasFooter?: boolean;
  hasHeader?: boolean;
  name: string;
  remarketingTag?: object;
  statusCode?: number;
  traits?: object;
  hideIntercom?: boolean;
  activeFonts?: FontInfo[];
  fixedFooterContent?: React.ReactNode;
  showBottomNav?: boolean;
  showMiddleMenu?: boolean;
  showSearchBar?: boolean;
  searchBarElement?: React.ReactNode;
  showWeWantToPay?: boolean;
  showLogo?: boolean;
  hasStickyNav?: boolean;
  hasProgressBar?: boolean;
  disableScrollToTop?: boolean;
  advertiserLogo?: string;
  showActions?: boolean;
  showMobileDrawerMenu?: boolean;
  flexContent?: boolean;
}

export const Page: React.FC<Props> = ({
  activeFonts,
  category = OTHER,
  children,
  fixedFooterContent,
  globalBanner,
  hasFooter = true,
  hasHeader = true,
  hideIntercom,
  name,
  noFollowFooter = true,
  pageBanner,
  remarketingTag,
  showBottomNav,
  showMiddleMenu = true,
  showSearchBar,
  searchBarElement,
  showWeWantToPay = false,
  showLogo = true,
  hasStickyNav,
  statusCode = 200,
  traits = {},
  hasProgressBar,
  disableScrollToTop = false,
  advertiserLogo,
  showMobileDrawerMenu = true,
  showActions = true,
  flexContent = false,
}) => {
  const location = useLocation();
  const { lg } = useMediaQuery();
  const { bookingFlowDropOffPoll } = useFeatureFlags();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const language = useSelector(getCurrentLanguage);
  const dispatch = useDispatch();
  const services = useServices();

  React.useEffect(() => {
    services.segment.init({ withIntercom: !hideIntercom });
  }, [services.segment]);

  React.useEffect(() => {
    if (!process.browser) {
      return;
    }

    const eventHandler = () => {
      dispatch(
        onPageMount({
          category,
          name,
          traits: { ...traits, language },
          remarketingTag,
          hideIntercom: hideIntercom || !lg,
        }),
      );
    };

    // fix analytics issue with back navigation reporting incorrect URL
    setTimeout(eventHandler, 200);
  }, []);

  React.useEffect(() => {
    if (
      bookingFlowDropOffPoll === 'on' &&
      !location.pathname.includes('my/book/contact')
    ) {
      const shouldShowHjPoll = getFromStorage('fromBookingFlow');

      if (shouldShowHjPoll) {
        dispatch(triggerHotjar('bookingflow_dropoff_poll'));
        removeFromStorage('fromBookingFlow');
      }
    }
  }, []);

  React.useEffect(() => {
    if (location.pathname.includes('my/book/contact')) return;

    const shouldShowHotjarPoll = getFromStorage('unregisteredBookingFlow');
    if (shouldShowHotjarPoll) {
      dispatch(triggerHotjar('unregistered_bookingflow_dropoff_poll'));
      removeFromStorage('unregisteredBookingFlow');
    }
  }, []);

  React.useEffect(() => {
    if (location.action === 'PUSH' && !disableScrollToTop) {
      window.scrollTo(0, 0);
    }
  }, [
    location.pathname,
    location.query.page,
    location.action,
    disableScrollToTop,
  ]);

  return (
    <PageProvider name={name} category={category}>
      <StatusCode status={statusCode} />
      <PageLayout
        activeFonts={activeFonts}
        fixedFooterContent={fixedFooterContent}
        globalBanner={globalBanner}
        hasFooter={hasFooter}
        hasHeader={hasHeader}
        hasProgressBar={hasProgressBar}
        hasStickyNav={hasStickyNav}
        isAuthenticated={isAuthenticated}
        noFollowFooter={noFollowFooter}
        pageBanner={pageBanner}
        showBottomNav={showBottomNav}
        showMiddleMenu={showMiddleMenu}
        showSearchBar={showSearchBar}
        searchBarElement={searchBarElement}
        showWeWantToPay={showWeWantToPay}
        showActions={showActions}
        showLogo={showLogo}
        showMobileDrawerMenu={showMobileDrawerMenu}
        advertiserLogo={advertiserLogo}
        flexContent={flexContent}
      >
        {children}
      </PageLayout>
    </PageProvider>
  );
};

export default Page;
